import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { isElementOfType } from 'react-dom/test-utils';
import { Button } from '../../controls/button';
import { VerticalPanel } from '../verticalpanel';
import { HorizontalPanel } from '../horizontalpanel';
import { Label } from '../../display/label';
import { SubmitButton } from '../../data-entry/submit-button';
import { Panel } from '../panel';
import { FormPanelGroup } from '../../data-entry/form-panel-group';

/**
 * {@docCategory LabelPanelGroup}
 */
export interface LabelPanelGroupProps {

   /**
    * Set this property to allow wrapping of the items to the next row.  This behavior is useful
    * for when items need to stay together "like columns in a grid," but will generally only
    * look good if no inline labels are allowed.
    *
    * @defaultvalue false
    */
   allowWrap?: boolean;

   /**
    * Whether or not to inline the labels of the items.  When in a LabelPanel, this property is
    * controlled by the LabelPanel.
    *
    * @defaultvalue true
    */
   inline?: boolean;

   /**
    * Set this property to override the width of the primary left side label when labels are inlined.
    * When in a LabelPanel, this property is controlled by the LabelPanel.
    *
    * @defaultvalue 250
    */
   maxLabelWidth?: number;

   /**
    * Whether or not to layout the items vertically.  When in a LabelPanel, this property is
    * controlled by the LabelPanel.
    *
    * @defaultvalue false
    */
   vertical?: boolean;

   /**
    * Whether or not to horizontal fill label content
    *
    * @defaultvalue false
    */
   horizontalFillLabelContent?: boolean;

   /**
    * Whether or not to force a large vertical gap size.  This is useful for situations where you
    * have labeled fields and need a larger vertical gap size.
    */
   useLargeVerticalGapSize?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

export const DEFAULT_LABEL_PANEL_GROUP_MAX_LABEL_WIDTH = 250;

export const LabelPanelGroup = (props: React.PropsWithChildren<LabelPanelGroupProps>) => {

   return (
      <Panel
         className={`uf-label-panel-group ${props.className || ''}`}
         verticalFill={false}
         horizontalFill={true}
         style={props.style}
      >
         <LabelPanelGroupContent
            vertical={props.vertical}
            allowWrap={props.allowWrap}
            inline={props.inline}
            horizontalFillLabelContent={props.horizontalFillLabelContent}
            useLargeVerticalGapSize={props.useLargeVerticalGapSize}
            maxLabelWidth={props.maxLabelWidth}
         >
            {props.children}
         </LabelPanelGroupContent>
      </Panel>
   );
};

const LabelPanelGroupContent = (props: React.PropsWithChildren<LabelPanelGroupProps>) => {

   const inlineLabels = props.inline === undefined ? true : props.inline;
   const maxLabelWidth = props.maxLabelWidth || DEFAULT_LABEL_PANEL_GROUP_MAX_LABEL_WIDTH;
   const horizontalFillLabelContent = props.horizontalFillLabelContent === undefined ? false : props.horizontalFillLabelContent;

   if (props.vertical === true) {
         return (
            <VerticalPanel
               className={'uf-label-panel-group-content uf-label-panel-group-content-vertical'+(inlineLabels ? ' uf-label-panel-group-content-inline' : '')}
               verticalFill={false}
               horizontalFill={true}
               gap={(!inlineLabels || props.useLargeVerticalGapSize) ? 'medium' : false}
            >
               {addAttributes(props.children, true, inlineLabels, props.maxLabelWidth, false, props.horizontalFillLabelContent)}
            </VerticalPanel>
         );
   }
   else if (!props.allowWrap) {
      return (
         <HorizontalPanel
            className={'uf-label-panel-group-content uf-label-panel-group-content-horizontal uf-label-panel-group-content-horizontal-nowrap'+(inlineLabels ? ' uf-label-panel-group-content-inline' : '')}
            horizontalFill={true}
            gap={inlineLabels ? 'medium' : 'small'}
            nowrap={true}
            verticalAlign={"top"}
         >
            {addAttributes(props.children, false, inlineLabels, props.maxLabelWidth, true, props.horizontalFillLabelContent)}
         </HorizontalPanel>
      );
   }
   else {
      return (
         <HorizontalPanel
            className={'uf-label-panel-group-content uf-label-panel-group-content-horizontal'+(inlineLabels ? ' uf-label-panel-group-content-inline' : '')}
            horizontalFill={true}
            gap={inlineLabels ? 'medium' : 'small'}
            verticalAlign={"top"}
         >
            {addAttributes(props.children, false, inlineLabels, props.maxLabelWidth, false, props.horizontalFillLabelContent)}
         </HorizontalPanel>
      );
   }
};

interface InlineType {
   inline?: boolean;
   horizontalFill?: boolean;
   horizontalFillLabelContent?: boolean;
   maxLabelWidth?: string | number;
   disableLabelWordWrap?: boolean;
}

const addAttributes = (children: ReactNode | undefined, vertical: boolean, inline: boolean, inlineLabelWidth?: number, isNoWrap?: boolean, horizontalFillLabelContent?: boolean) => {

   if (children === undefined) {
      return children;
   }

   const labelWidthForInline = (inline ? inlineLabelWidth : undefined);

   let appliedFirstChild = false;

   const childrenArray: ReactElement[] = [];

   React.Children.forEach(children, (child: any) => {
      if (isElementOfType(child, React.Fragment)) {
         child = (child.props as any).children;
      }
      childrenArray.push(child);
   });

   return React.Children.map(childrenArray, (child: any) => {
      if (!React.isValidElement<InlineType>(child)) {
         return child;
      }

      if (isElementOfType(child, Button) || isElementOfType(child, SubmitButton)) {

         const padding = (inline) ? undefined : (vertical ? '5px' : '26px');
         const paddingStyle = { paddingTop: padding };
         child = (
            <Label>
               <div style={paddingStyle}>
                  {React.cloneElement(child, { style: { maxHeight: 30} })}
               </div>
            </Label>);
      }

      if (!vertical && appliedFirstChild) {

         const maxLabelWidth = child.props.maxLabelWidth !== undefined ? child.props.maxLabelWidth : 'max-content';
         const props: InlineType = { inline: inline, maxLabelWidth: maxLabelWidth, disableLabelWordWrap: true };

         if (horizontalFillLabelContent) {
            if (isElementOfType(child, LabelPanelGroup) || isElementOfType(child, FormPanelGroup)) {
               props.horizontalFillLabelContent = horizontalFillLabelContent;
            }
            else {
               props.horizontalFill = horizontalFillLabelContent;
            }
         }
         return React.cloneElement(child, props);
      }

      appliedFirstChild = true;
      const props: InlineType = { inline: inline, maxLabelWidth: labelWidthForInline, disableLabelWordWrap: isNoWrap };
      if (horizontalFillLabelContent) {
         if (isElementOfType(child, LabelPanelGroup) || isElementOfType(child, FormPanelGroup)) {
            props.horizontalFillLabelContent = horizontalFillLabelContent;
         }
         else {
            props.horizontalFill = horizontalFillLabelContent;
         }
      }
      return React.cloneElement(child, props);
   });
};
